import React, { useEffect, useState } from "react";
import PageShell from "./PageShell";
import "./PortalUI.css";
import {
    API_URL,
    getAuphonicPresets,
    getUsers,
    getGlobalChangeLogs,
    getShowNotesModels,
    getShowNotesSettings,
    start2FASetup,
    verify2FASetup,
    disable2FA,
    updateShowNotesSettings,
} from "./api";
import {
    getThemePreference,
    applyThemePreference,
} from "./theme";

function Settings({ token, onLogout, embedded = false }) {
    const [user, setUser] = useState(null);
    const [error, setError] = useState("");
    const [loadingUser, setLoadingUser] = useState(true);

    // Admin user management
    const [users, setUsers] = useState([]);
    const [adminError, setAdminError] = useState("");
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState("");
    const [historyLogs, setHistoryLogs] = useState([]);
    const [selectedHistoryLogId, setSelectedHistoryLogId] = useState(null);
    const [historyFilters, setHistoryFilters] = useState({
        source: "",
        action: "",
        changed_by_user_id: "",
        date_from: "",
        date_to: "",
        search: "",
        limit: "100",
    });
    const [showNotesSettingsForm, setShowNotesSettingsForm] = useState({
        show_notes_provider: "openai",
        openai_model: "",
        openai_prompt: "",
        grok_model: "",
        grok_prompt: "",
        openai_api_key: "",
        has_openai_api_key: false,
        openai_api_key_source: "none",
        clear_openai_api_key: false,
        grok_api_key: "",
        has_grok_api_key: false,
        grok_api_key_source: "none",
        clear_grok_api_key: false,
        auphonic_api_key: "",
        has_auphonic_api_key: false,
        auphonic_api_key_source: "none",
        auphonic_processing_preset: "",
        clear_auphonic_api_key: false,
    });
    const [showNotesSettingsLoading, setShowNotesSettingsLoading] = useState(false);
    const [showNotesSettingsSaving, setShowNotesSettingsSaving] = useState(false);
    const [showNotesSettingsError, setShowNotesSettingsError] = useState("");
    const [showNotesSettingsSuccess, setShowNotesSettingsSuccess] = useState("");
    const [openAiShowNotesModels, setOpenAiShowNotesModels] = useState([]);
    const [grokShowNotesModels, setGrokShowNotesModels] = useState([]);
    const [openAiShowNotesModelChoice, setOpenAiShowNotesModelChoice] = useState("");
    const [grokShowNotesModelChoice, setGrokShowNotesModelChoice] = useState("");
    const [auphonicPresets, setAuphonicPresets] = useState([]);
    const [auphonicPresetsLoading, setAuphonicPresetsLoading] = useState(false);
    const [auphonicPresetsError, setAuphonicPresetsError] = useState("");

    const getUserDisplayName = (u) =>
        [u?.first_name, u?.surname].filter(Boolean).join(" ") ||
        u?.name ||
        u?.email ||
        u?.username ||
        "Unknown user";

    const sortedUsersForAdminView = [...users].sort((a, b) => {
        const roleRank = (role) => {
            if (role === "admin") return 0;
            if (role === "client") return 1;
            return 2;
        };

        const roleDiff = roleRank(a.role) - roleRank(b.role);
        if (roleDiff !== 0) return roleDiff;
        return getUserDisplayName(a).localeCompare(getUserDisplayName(b), undefined, {
            sensitivity: "base",
        });
    });

    // 2FA UI state (for current user)
    const [twoFA, setTwoFA] = useState({
        settingUp: false,
        loading: false,
        qrDataUrl: "",
        code: "",
        error: "",
        success: "",
    });

    // Theme preference state (system | light | dark)
    const [themePreference, setThemePreference] = useState("system");

    // ---- LOAD CURRENT USER ----
    useEffect(() => {
        async function loadUser() {
            if (!token) {
                setError("No token – please log in again.");
                setLoadingUser(false);
                return;
            }

            try {
                const res = await fetch(`${API_URL}/auth/me`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const data = await res.json();

                if (!res.ok || !data.ok) {
                    setError(data.message || "Failed to load user settings");
                } else {
                    setUser(data.user);
                }
            } catch (err) {
                console.error(err);
                setError("Error loading settings");
            } finally {
                setLoadingUser(false);
            }
        }

        loadUser();
    }, [token]);

    // ---- INIT THEME PREFERENCE FROM STORAGE / SYSTEM ----
    useEffect(() => {
        const pref = getThemePreference(); // "system" | "light" | "dark"
        setThemePreference(pref);
        applyThemePreference(pref); // sets data-theme on <html> + saves to localStorage
    }, []);

    // ---- LOAD USERS (ADMIN) ----
    useEffect(() => {
        async function loadUsers() {
            if (!user || user.role !== "admin") return;

            setLoadingUsers(true);
            setAdminError("");

            try {
                const list = await getUsers();
                setUsers(list);
            } catch (err) {
                console.error(err);
                setAdminError(err.message || "Failed to load users");
            } finally {
                setLoadingUsers(false);
            }
        }

        loadUsers();
    }, [user]);

    useEffect(() => {
        if (!user || user.role !== "admin") return;
        const initialFilters = {
            source: "",
            action: "",
            changed_by_user_id: "",
            date_from: "",
            date_to: "",
            search: "",
            limit: "100",
        };
        loadGlobalHistory(initialFilters);
    }, [user]);

    useEffect(() => {
        if (!user || user.role !== "admin") return;
        const timer = setTimeout(() => {
            setHistoryLoading(true);
            setHistoryError("");
            getGlobalChangeLogs(historyFilters)
                .then((res) => {
                    if (!res?.ok) {
                        throw new Error(
                            res?.message || "Failed to load change history"
                        );
                    }
                    const rows = Array.isArray(res.logs) ? res.logs : [];
                    setHistoryLogs(rows);
                    setSelectedHistoryLogId((current) =>
                        rows.some((x) => x.id === current) ? current : null
                    );
                })
                .catch((err) => {
                    console.error(err);
                    setHistoryError(err.message || "Failed to load change history");
                })
                .finally(() => {
                    setHistoryLoading(false);
                });
        }, 250);
        return () => clearTimeout(timer);
    }, [historyFilters, user]);

    useEffect(() => {
        async function loadShowNotesAdminSettings() {
            if (!user || user.role !== "admin") return;
            setShowNotesSettingsLoading(true);
            setShowNotesSettingsError("");
            setAuphonicPresetsLoading(true);
            setAuphonicPresetsError("");
            try {
                const settings = await getShowNotesSettings();
                let openAiModels = [];
                let grokModels = [];
                let presets = [];
                try {
                    openAiModels = await getShowNotesModels("openai");
                } catch {
                    // Model discovery depends on a configured API key.
                    // Keep settings usable even when the model list cannot be loaded yet.
                    openAiModels = [];
                }
                try {
                    grokModels = await getShowNotesModels("grok");
                } catch {
                    // Grok may not be configured yet; keep manual entry available.
                    grokModels = [];
                }
                try {
                    presets = await getAuphonicPresets();
                } catch (err) {
                    console.error(err);
                    setAuphonicPresetsError(
                        err.message || "Failed to load Auphonic presets"
                    );
                    presets = [];
                }
                const currentOpenAiModel = String(
                    settings?.openai_model || settings?.model || ""
                );
                const currentGrokModel = String(settings?.grok_model || "");
                const availableOpenAiModels = Array.isArray(openAiModels)
                    ? openAiModels
                    : [];
                const availableGrokModels = Array.isArray(grokModels)
                    ? grokModels
                    : [];
                const hasCurrentOpenAiModel =
                    availableOpenAiModels.includes(currentOpenAiModel);
                const hasCurrentGrokModel =
                    availableGrokModels.includes(currentGrokModel);
                setShowNotesSettingsForm({
                    show_notes_provider:
                        String(settings?.show_notes_provider || "").toLowerCase() ===
                        "grok"
                            ? "grok"
                            : "openai",
                    openai_model: currentOpenAiModel,
                    openai_prompt: String(
                        settings?.openai_prompt || settings?.prompt || ""
                    ),
                    grok_model: currentGrokModel,
                    grok_prompt: String(
                        settings?.grok_prompt || settings?.prompt || ""
                    ),
                    openai_api_key: "",
                    has_openai_api_key: Boolean(settings?.has_openai_api_key),
                    openai_api_key_source: String(
                        settings?.openai_api_key_source || "none"
                    ),
                    clear_openai_api_key: false,
                    grok_api_key: "",
                    has_grok_api_key: Boolean(settings?.has_grok_api_key),
                    grok_api_key_source: String(
                        settings?.grok_api_key_source || "none"
                    ),
                    clear_grok_api_key: false,
                    auphonic_api_key: "",
                    has_auphonic_api_key: Boolean(settings?.has_auphonic_api_key),
                    auphonic_api_key_source: String(
                        settings?.auphonic_api_key_source || "none"
                    ),
                    auphonic_processing_preset: String(
                        settings?.auphonic_processing_preset || ""
                    ),
                    clear_auphonic_api_key: false,
                });
                setOpenAiShowNotesModels(availableOpenAiModels);
                setGrokShowNotesModels(availableGrokModels);
                setAuphonicPresets(Array.isArray(presets) ? presets : []);
                setOpenAiShowNotesModelChoice(
                    hasCurrentOpenAiModel
                        ? currentOpenAiModel
                        : currentOpenAiModel
                          ? "__custom__"
                          : ""
                );
                setGrokShowNotesModelChoice(
                    hasCurrentGrokModel
                        ? currentGrokModel
                        : currentGrokModel
                          ? "__custom__"
                          : ""
                );
            } catch (err) {
                console.error(err);
                setShowNotesSettingsError(
                    err.message || "Failed to load show notes settings"
                );
            } finally {
                setShowNotesSettingsLoading(false);
                setAuphonicPresetsLoading(false);
            }
        }
        loadShowNotesAdminSettings();
    }, [user]);

    // ---------- THEME HANDLERS ----------

    const handleThemeChange = (e) => {
        const pref = e.target.value; // "system" | "light" | "dark"
        setThemePreference(pref);
        applyThemePreference(pref);
    };

    const handleHistoryFilterChange = (e) => {
        const { name, value } = e.target;
        setHistoryFilters((prev) => ({ ...prev, [name]: value }));
    };

    const loadGlobalHistory = async (filters = historyFilters) => {
        setHistoryLoading(true);
        setHistoryError("");
        try {
            const res = await getGlobalChangeLogs(filters);
            if (!res?.ok) {
                throw new Error(res?.message || "Failed to load change history");
            }
            const rows = Array.isArray(res.logs) ? res.logs : [];
            setHistoryLogs(rows);
            setSelectedHistoryLogId((current) =>
                rows.some((x) => x.id === current) ? current : null
            );
        } catch (err) {
            console.error(err);
            setHistoryError(err.message || "Failed to load change history");
        } finally {
            setHistoryLoading(false);
        }
    };

    const clearHistoryFilters = () => {
        const cleared = {
            source: "",
            action: "",
            changed_by_user_id: "",
            date_from: "",
            date_to: "",
            search: "",
            limit: "100",
        };
        setHistoryFilters(cleared);
        loadGlobalHistory(cleared);
    };

    const handleShowNotesSettingsChange = (e) => {
        const { name, value } = e.target;
        setShowNotesSettingsForm((prev) => ({ ...prev, [name]: value }));
        if (showNotesSettingsSuccess) {
            setShowNotesSettingsSuccess("");
        }
    };

    const handleShowNotesModelChoiceChange = (provider, e) => {
        const value = String(e.target.value || "");
        const modelKey = provider === "grok" ? "grok_model" : "openai_model";
        if (provider === "grok") {
            setGrokShowNotesModelChoice(value);
        } else {
            setOpenAiShowNotesModelChoice(value);
        }
        if (value && value !== "__custom__") {
            setShowNotesSettingsForm((prev) => ({ ...prev, [modelKey]: value }));
        }
        if (showNotesSettingsSuccess) {
            setShowNotesSettingsSuccess("");
        }
    };

    const handleSaveShowNotesSettings = async (e) => {
        e.preventDefault();
        if (!user || user.role !== "admin") return;
        setShowNotesSettingsSaving(true);
        setShowNotesSettingsError("");
        setShowNotesSettingsSuccess("");
        try {
            const saved = await updateShowNotesSettings({
                show_notes_provider:
                    String(showNotesSettingsForm.show_notes_provider || "").trim() ===
                    "grok"
                        ? "grok"
                        : "openai",
                openai_model: String(showNotesSettingsForm.openai_model || "").trim(),
                openai_prompt: String(showNotesSettingsForm.openai_prompt || ""),
                grok_model: String(showNotesSettingsForm.grok_model || "").trim(),
                grok_prompt: String(showNotesSettingsForm.grok_prompt || ""),
                openai_api_key: String(showNotesSettingsForm.openai_api_key || ""),
                clear_openai_api_key: Boolean(
                    showNotesSettingsForm.clear_openai_api_key
                ),
                grok_api_key: String(showNotesSettingsForm.grok_api_key || ""),
                clear_grok_api_key: Boolean(
                    showNotesSettingsForm.clear_grok_api_key
                ),
                auphonic_api_key: String(showNotesSettingsForm.auphonic_api_key || ""),
                auphonic_processing_preset: String(
                    showNotesSettingsForm.auphonic_processing_preset || ""
                ).trim(),
                clear_auphonic_api_key: Boolean(
                    showNotesSettingsForm.clear_auphonic_api_key
                ),
            });
            const savedOpenAiModel = String(
                saved?.openai_model || saved?.model || ""
            );
            const savedGrokModel = String(saved?.grok_model || "");
            setShowNotesSettingsForm({
                show_notes_provider:
                    String(saved?.show_notes_provider || "").toLowerCase() === "grok"
                        ? "grok"
                        : "openai",
                openai_model: savedOpenAiModel,
                openai_prompt: String(saved?.openai_prompt || saved?.prompt || ""),
                grok_model: savedGrokModel,
                grok_prompt: String(saved?.grok_prompt || saved?.prompt || ""),
                openai_api_key: "",
                has_openai_api_key: Boolean(saved?.has_openai_api_key),
                openai_api_key_source: String(
                    saved?.openai_api_key_source || "none"
                ),
                clear_openai_api_key: false,
                grok_api_key: "",
                has_grok_api_key: Boolean(saved?.has_grok_api_key),
                grok_api_key_source: String(saved?.grok_api_key_source || "none"),
                clear_grok_api_key: false,
                auphonic_api_key: "",
                has_auphonic_api_key: Boolean(saved?.has_auphonic_api_key),
                auphonic_api_key_source: String(
                    saved?.auphonic_api_key_source || "none"
                ),
                auphonic_processing_preset: String(
                    saved?.auphonic_processing_preset || ""
                ),
                clear_auphonic_api_key: false,
            });
            setOpenAiShowNotesModelChoice(
                !savedOpenAiModel
                    ? ""
                    : openAiShowNotesModels.includes(savedOpenAiModel)
                    ? savedOpenAiModel
                    : "__custom__"
            );
            setGrokShowNotesModelChoice(
                !savedGrokModel
                    ? ""
                    : grokShowNotesModels.includes(savedGrokModel)
                    ? savedGrokModel
                    : "__custom__"
            );
            setShowNotesSettingsSuccess("Show Notes settings saved.");
        } catch (err) {
            console.error(err);
            setShowNotesSettingsError(
                err.message || "Failed to save show notes settings"
            );
        } finally {
            setShowNotesSettingsSaving(false);
        }
    };

    const summarizeLogDiff = (rawDiff) => {
        if (!rawDiff) return "—";
        let parsed = rawDiff;
        if (typeof rawDiff === "string") {
            try {
                parsed = JSON.parse(rawDiff);
            } catch {
                return rawDiff.slice(0, 140);
            }
        }

        const fields =
            parsed?.fields && typeof parsed.fields === "object"
                ? parsed.fields
                : null;
        if (!fields) return "Details available";

        const keys = Object.keys(fields);
        if (!keys.length) return "No field changes";
        if (keys.length === 1) return `1 field changed: ${keys[0]}`;
        return `${keys.length} fields changed: ${keys.slice(0, 3).join(", ")}${
            keys.length > 3 ? " …" : ""
        }`;
    };

    const parseHistoryDiff = (rawDiff) => {
        if (!rawDiff) return null;
        if (typeof rawDiff === "string") {
            try {
                return JSON.parse(rawDiff);
            } catch {
                return { raw: rawDiff };
            }
        }
        return rawDiff;
    };

    // ---- 2FA HANDLERS (current user) ----

    const handleStart2FA = async () => {
        setTwoFA((prev) => ({
            ...prev,
            loading: true,
            error: "",
            success: "",
        }));

        try {
            const data = await start2FASetup();

            if (!data || !data.ok) {
                throw new Error(data?.message || "Failed to start 2FA setup");
            }

            setTwoFA((prev) => ({
                ...prev,
                loading: false,
                settingUp: true,
                qrDataUrl: data.qrDataUrl || "",
            }));
        } catch (err) {
            console.error(err);
            setTwoFA((prev) => ({
                ...prev,
                loading: false,
                error: err.message || "Failed to start 2FA setup",
            }));
        }
    };

    const handleVerify2FA = async (e) => {
        e.preventDefault();

        setTwoFA((prev) => ({
            ...prev,
            loading: true,
            error: "",
            success: "",
        }));

        try {
            const data = await verify2FASetup(twoFA.code);

            if (!data || !data.ok) {
                throw new Error(data?.message || "Failed to verify 2FA code");
            }

            // Mark user as 2FA enabled in local state
            setUser((u) => (u ? { ...u, two_factor_enabled: 1 } : u));

            setTwoFA({
                settingUp: false,
                loading: false,
                qrDataUrl: "",
                code: "",
                error: "",
                success: "Two-factor authentication has been enabled.",
            });
        } catch (err) {
            console.error(err);
            setTwoFA((prev) => ({
                ...prev,
                loading: false,
                error: err.message || "Failed to verify 2FA code",
            }));
        }
    };

    const handleTwoFACodeChange = (e) => {
        const { value } = e.target;
        setTwoFA((prev) => ({ ...prev, code: value }));
    };

    const handleDisable2FA = async () => {
        setTwoFA((prev) => ({
            ...prev,
            loading: true,
            error: "",
            success: "",
        }));

        try {
            const data = await disable2FA();

            if (!data || !data.ok) {
                throw new Error(data?.message || "Failed to disable 2FA");
            }

            // Update current user state
            setUser((u) => (u ? { ...u, two_factor_enabled: 0 } : u));

            setTwoFA((prev) => ({
                ...prev,
                loading: false,
                settingUp: false,
                qrDataUrl: "",
                code: "",
                error: "",
                success: "Two-factor authentication has been disabled.",
            }));
        } catch (err) {
            console.error(err);
            setTwoFA((prev) => ({
                ...prev,
                loading: false,
                error: err.message || "Failed to disable 2FA",
            }));
        }
    };

    return (
        <PageShell embedded={embedded} onLogout={onLogout}>
            <div className="portal-page">
                {/* Page header */}
                <div className="portal-page__header">
                    <div>
                        <h2 className="portal-page__title">Settings</h2>
                        <p className="portal-page__subtitle">
                            Manage your account, security and appearance.
                        </p>
                    </div>
                </div>

                {error && <div className="alert alert--error">{error}</div>}

                {loadingUser && !error && <p>Loading settings…</p>}

                {/* MAIN CONTENT AFTER USER LOADED */}
                {user && (
                    <>
                        {/* Profile + 2FA + Appearance cards */}
                        <div className="settings-grid">
                            {/* Profile card */}
                            <div className="card">
                                <h3 className="card__title">Your Profile</h3>
                                <p className="card__subtitle">
                                    Basic details about your portal account.
                                </p>

                                <div style={{ marginTop: 10, fontSize: 14 }}>
                                    <div className="card-info-row">
                                        <span className="card-info-row__label">
                                            First name
                                        </span>
                                        <span className="card-info-row__value">
                                            {user.first_name || user.name || "—"}
                                        </span>
                                    </div>
                                    <div className="card-info-row">
                                        <span className="card-info-row__label">
                                            Surname
                                        </span>
                                        <span className="card-info-row__value">
                                            {user.surname || "—"}
                                        </span>
                                    </div>
                                    <div className="card-info-row">
                                        <span className="card-info-row__label">
                                            Email
                                        </span>
                                        <span className="card-info-row__value">
                                            {user.email || "—"}
                                        </span>
                                    </div>
                                    <div className="card-info-row">
                                        <span className="card-info-row__label">
                                            Role
                                        </span>
                                        <span
                                            className={
                                                "pill " +
                                                (user.role === "admin"
                                                    ? "pill--role-admin"
                                                    : "pill--role-client")
                                            }
                                        >
                                            {user.role}
                                        </span>
                                    </div>
                                    {user.created_at && (
                                        <div className="card-info-row">
                                            <span className="card-info-row__label">
                                                Account Created
                                            </span>
                                            <span className="card-info-row__value">
                                                {new Date(
                                                    user.created_at
                                                ).toLocaleString()}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 2FA card */}
                            <div className="card">
                                <h3 className="card__title">
                                    Two-factor authentication (2FA)
                                </h3>
                                <p className="card__subtitle">
                                    Add an extra layer of security to your login.
                                </p>

                                {twoFA.success && (
                                    <div className="alert alert--success">
                                        {twoFA.success}
                                    </div>
                                )}
                                {twoFA.error && (
                                    <div className="alert alert--error">
                                        {twoFA.error}
                                    </div>
                                )}

                                {!user.two_factor_enabled ? (
                                    <>
                                        {!twoFA.settingUp && (
                                            <div style={{ marginTop: 10 }}>
                                                <p className="text-description">
                                                    2FA is currently{" "}
                                                    <strong>disabled</strong> for
                                                    your account.
                                                </p>
                                                <button
                                                    onClick={handleStart2FA}
                                                    disabled={twoFA.loading}
                                                    className="btn btn--primary"
                                                >
                                                    {twoFA.loading
                                                        ? "Starting 2FA…"
                                                        : "Enable 2FA"}
                                                </button>
                                            </div>
                                        )}

                                        {twoFA.settingUp && (
                                            <div style={{ marginTop: 10 }}>
                                                <p className="text-description">
                                                    Scan this QR code with your
                                                    authenticator app (Google
                                                    Authenticator, Microsoft
                                                    Authenticator, etc.), then
                                                    enter the 6-digit code to
                                                    confirm.
                                                </p>

                                                {twoFA.qrDataUrl && (
                                                    <div
                                                        style={{
                                                            marginTop: 10,
                                                            marginBottom: 10,
                                                        }}
                                                    >
                                                        <img
                                                            src={twoFA.qrDataUrl}
                                                            alt="2FA QR code"
                                                            style={{
                                                                width: 200,
                                                                height: 200,
                                                                borderRadius: 8,
                                                                border:
                                                                    "1px solid #e5e7eb",
                                                            }}
                                                        />
                                                    </div>
                                                )}

                                                <form
                                                    onSubmit={handleVerify2FA}
                                                    style={{
                                                        display: "flex",
                                                        gap: 10,
                                                        alignItems: "flex-end",
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    <div className="form-field">
                                                        <span className="form-field__label">
                                                            6-digit code
                                                        </span>
                                                        <input
                                                            type="text"
                                                            value={twoFA.code}
                                                            onChange={
                                                                handleTwoFACodeChange
                                                            }
                                                            maxLength={6}
                                                            required
                                                            className="form-field__input"
                                                            style={{
                                                                width: 120,
                                                                textAlign: "center",
                                                            }}
                                                        />
                                                    </div>
                                                    <button
                                                        type="submit"
                                                        disabled={twoFA.loading}
                                                        className="btn btn--primary"
                                                    >
                                                        {twoFA.loading
                                                            ? "Verifying…"
                                                            : "Verify & Enable 2FA"}
                                                    </button>
                                                </form>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div style={{ marginTop: 10 }}>
                                        <p className="text-description">
                                            2FA is{" "}
                                            <strong>enabled</strong> on your
                                            account ✅
                                        </p>
                                        {user.role === "admin" ? (
                                            <button
                                                onClick={handleDisable2FA}
                                                disabled={twoFA.loading}
                                                className="btn btn--secondary"
                                            >
                                                {twoFA.loading
                                                    ? "Disabling…"
                                                    : "Disable 2FA"}
                                            </button>
                                        ) : (
                                            <p className="text-description">
                                                Contact Urban Podcasts if you need to
                                                reset your two-factor setup.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Appearance / Theme card */}
                            <div className="card">
                                <h3 className="card__title">Appearance</h3>
                                <p className="card__subtitle">
                                    Choose how the portal should look on this device.
                                </p>

                                <div style={{ marginTop: 10 }}>
                                    <label className="form-field__checkbox-row">
                                        <input
                                            type="radio"
                                            name="themePref"
                                            value="system"
                                            checked={themePreference === "system"}
                                            onChange={handleThemeChange}
                                        />
                                        <span className="form-field__label">
                                            System default
                                        </span>
                                    </label>

                                    <label className="form-field__checkbox-row">
                                        <input
                                            type="radio"
                                            name="themePref"
                                            value="light"
                                            checked={themePreference === "light"}
                                            onChange={handleThemeChange}
                                        />
                                        <span className="form-field__label">
                                            Light mode
                                        </span>
                                    </label>

                                    <label className="form-field__checkbox-row">
                                        <input
                                            type="radio"
                                            name="themePref"
                                            value="dark"
                                            checked={themePreference === "dark"}
                                            onChange={handleThemeChange}
                                        />
                                        <span className="form-field__label">
                                            Dark mode
                                        </span>
                                    </label>

                                    <p
                                        className="text-description"
                                        style={{ marginTop: 8 }}
                                    >
                                        Your choice is saved for this browser and
                                        overrides the system setting when set to
                                        Light or Dark.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* ---- ADMIN OVERALL CHANGE HISTORY ---- */}
                        {user.role === "admin" && (
                            <div style={{ marginTop: 24 }}>
                                <div className="card" style={{ marginTop: 18 }}>
                                    <h3 className="card__title">
                                        OpenAI, Grok and Auphonic
                                    </h3>
                                    <p className="card__subtitle">
                                        Configure OpenAI/Grok show notes and Auphonic
                                        processing defaults.
                                    </p>

                                    {showNotesSettingsError && (
                                        <div className="alert alert--error">
                                            {showNotesSettingsError}
                                        </div>
                                    )}
                                    {showNotesSettingsSuccess && (
                                        <div className="alert alert--success">
                                            {showNotesSettingsSuccess}
                                        </div>
                                    )}

                                    {showNotesSettingsLoading ? (
                                        <p style={{ marginTop: 10 }}>Loading settings…</p>
                                    ) : (
                                        <form
                                            onSubmit={handleSaveShowNotesSettings}
                                            style={{ marginTop: 10 }}
                                        >
                                            <label className="form-field">
                                                <span className="form-field__label">
                                                    Show notes provider
                                                </span>
                                                <select
                                                    name="show_notes_provider"
                                                    value={
                                                        showNotesSettingsForm.show_notes_provider
                                                    }
                                                    onChange={handleShowNotesSettingsChange}
                                                    className="form-field__select"
                                                    disabled={showNotesSettingsSaving}
                                                >
                                                    <option value="openai">OpenAI</option>
                                                    <option value="grok">Grok</option>
                                                </select>
                                            </label>

                                            <h4 style={{ margin: "0 0 8px 0" }}>
                                                OpenAI Show Notes
                                            </h4>
                                            <p
                                                className="text-description"
                                                style={{ margin: "0 0 10px 0" }}
                                            >
                                                Used when generating show notes from a
                                                transcript.
                                            </p>
                                            <label className="form-field">
                                                <span className="form-field__label">
                                                    OpenAI API key
                                                </span>
                                                <input
                                                    type="password"
                                                    name="openai_api_key"
                                                    value={showNotesSettingsForm.openai_api_key}
                                                    onChange={handleShowNotesSettingsChange}
                                                    className="form-field__input"
                                                    placeholder="Leave blank to keep existing key"
                                                    autoComplete="new-password"
                                                    disabled={showNotesSettingsSaving}
                                                />
                                                <p className="text-description" style={{ marginTop: 6 }}>
                                                    Stored key:{" "}
                                                    {showNotesSettingsForm.has_openai_api_key
                                                        ? `Configured (${showNotesSettingsForm.openai_api_key_source})`
                                                        : "Not configured"}
                                                </p>
                                            </label>
                                            <label className="form-field">
                                                <span className="form-field__label">Model</span>
                                                <select
                                                    value={openAiShowNotesModelChoice}
                                                    onChange={(e) =>
                                                        handleShowNotesModelChoiceChange(
                                                            "openai",
                                                            e
                                                        )
                                                    }
                                                    className="form-field__select"
                                                    disabled={
                                                        showNotesSettingsSaving ||
                                                        showNotesSettingsLoading
                                                    }
                                                >
                                                    <option value="">Select model</option>
                                                    {openAiShowNotesModels.map((modelId) => (
                                                        <option key={modelId} value={modelId}>
                                                            {modelId}
                                                        </option>
                                                    ))}
                                                    <option value="__custom__">
                                                        Custom model
                                                    </option>
                                                </select>
                                                {openAiShowNotesModelChoice === "__custom__" && (
                                                    <input
                                                        name="openai_model"
                                                        value={showNotesSettingsForm.openai_model}
                                                        onChange={handleShowNotesSettingsChange}
                                                        className="form-field__input"
                                                        placeholder="Enter custom model id"
                                                        disabled={showNotesSettingsSaving}
                                                        style={{ marginTop: 8 }}
                                                    />
                                                )}
                                            </label>
                                            <label className="form-field">
                                                <span className="form-field__label">Prompt</span>
                                                <textarea
                                                    name="openai_prompt"
                                                    value={showNotesSettingsForm.openai_prompt}
                                                    onChange={handleShowNotesSettingsChange}
                                                    rows={10}
                                                    className="form-field__textarea"
                                                    disabled={showNotesSettingsSaving}
                                                />
                                            </label>

                                            <hr style={{ margin: "16px 0" }} />

                                            <h4 style={{ margin: "0 0 8px 0" }}>
                                                Grok Show Notes
                                            </h4>
                                            <p
                                                className="text-description"
                                                style={{ margin: "0 0 10px 0" }}
                                            >
                                                Alternative provider for show notes
                                                generation.
                                            </p>
                                            <label className="form-field">
                                                <span className="form-field__label">
                                                    Grok API key
                                                </span>
                                                <input
                                                    type="password"
                                                    name="grok_api_key"
                                                    value={showNotesSettingsForm.grok_api_key}
                                                    onChange={handleShowNotesSettingsChange}
                                                    className="form-field__input"
                                                    placeholder="Leave blank to keep existing key"
                                                    autoComplete="new-password"
                                                    disabled={showNotesSettingsSaving}
                                                />
                                                <p className="text-description" style={{ marginTop: 6 }}>
                                                    Stored key:{" "}
                                                    {showNotesSettingsForm.has_grok_api_key
                                                        ? `Configured (${showNotesSettingsForm.grok_api_key_source})`
                                                        : "Not configured"}
                                                </p>
                                            </label>
                                            <label className="form-field">
                                                <span className="form-field__label">Model</span>
                                                <select
                                                    value={grokShowNotesModelChoice}
                                                    onChange={(e) =>
                                                        handleShowNotesModelChoiceChange(
                                                            "grok",
                                                            e
                                                        )
                                                    }
                                                    className="form-field__select"
                                                    disabled={
                                                        showNotesSettingsSaving ||
                                                        showNotesSettingsLoading
                                                    }
                                                >
                                                    <option value="">Select model</option>
                                                    {grokShowNotesModels.map((modelId) => (
                                                        <option key={modelId} value={modelId}>
                                                            {modelId}
                                                        </option>
                                                    ))}
                                                    <option value="__custom__">
                                                        Custom model
                                                    </option>
                                                </select>
                                                {grokShowNotesModelChoice === "__custom__" && (
                                                    <input
                                                        name="grok_model"
                                                        value={showNotesSettingsForm.grok_model}
                                                        onChange={handleShowNotesSettingsChange}
                                                        className="form-field__input"
                                                        placeholder="Enter custom model id"
                                                        disabled={showNotesSettingsSaving}
                                                        style={{ marginTop: 8 }}
                                                    />
                                                )}
                                            </label>
                                            <label className="form-field">
                                                <span className="form-field__label">Prompt</span>
                                                <textarea
                                                    name="grok_prompt"
                                                    value={showNotesSettingsForm.grok_prompt}
                                                    onChange={handleShowNotesSettingsChange}
                                                    rows={10}
                                                    className="form-field__textarea"
                                                    disabled={showNotesSettingsSaving}
                                                />
                                            </label>

                                            <hr style={{ margin: "16px 0" }} />

                                            <h4 style={{ margin: "0 0 8px 0" }}>
                                                Auphonic Processing
                                            </h4>
                                            <p
                                                className="text-description"
                                                style={{ margin: "0 0 10px 0" }}
                                            >
                                                Used for audio processing defaults in
                                                episode file actions.
                                            </p>
                                            <label className="form-field">
                                                <span className="form-field__label">
                                                    Auphonic API key
                                                </span>
                                                <input
                                                    type="password"
                                                    name="auphonic_api_key"
                                                    value={showNotesSettingsForm.auphonic_api_key}
                                                    onChange={handleShowNotesSettingsChange}
                                                    className="form-field__input"
                                                    placeholder="Leave blank to keep existing key"
                                                    autoComplete="new-password"
                                                    disabled={showNotesSettingsSaving}
                                                />
                                                <p className="text-description" style={{ marginTop: 6 }}>
                                                    Stored key:{" "}
                                                    {showNotesSettingsForm.has_auphonic_api_key
                                                        ? `Configured (${showNotesSettingsForm.auphonic_api_key_source})`
                                                        : "Not configured"}
                                                </p>
                                            </label>
                                            <label className="form-field">
                                                <span className="form-field__label">
                                                    Default Auphonic processing preset
                                                </span>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gap: 8,
                                                        alignItems: "center",
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    <select
                                                        className="form-field__select"
                                                        value={
                                                            showNotesSettingsForm.auphonic_processing_preset
                                                        }
                                                        onChange={(e) =>
                                                            setShowNotesSettingsForm((prev) => ({
                                                                ...prev,
                                                                auphonic_processing_preset:
                                                                    String(e.target.value || ""),
                                                            }))
                                                        }
                                                        disabled={
                                                            showNotesSettingsSaving ||
                                                            auphonicPresetsLoading ||
                                                            !auphonicPresets.length
                                                        }
                                                        style={{ minWidth: 240 }}
                                                    >
                                                        <option value="">
                                                            {auphonicPresetsLoading
                                                                ? "Loading presets…"
                                                                : auphonicPresets.length
                                                                ? "Select preset"
                                                                : "No presets loaded"}
                                                        </option>
                                                        {auphonicPresets.map((preset) => (
                                                            <option
                                                                key={String(preset.uuid || "")}
                                                                value={String(preset.uuid || "")}
                                                            >
                                                                {String(preset.name || preset.uuid)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {auphonicPresetsError && (
                                                    <p
                                                        className="text-description"
                                                        style={{ marginTop: 6, color: "#ef4444" }}
                                                    >
                                                    {auphonicPresetsError}
                                                </p>
                                            )}
                                            </label>
                                            <button
                                                type="submit"
                                                className="btn btn--primary"
                                                disabled={showNotesSettingsSaving}
                                            >
                                                {showNotesSettingsSaving
                                                    ? "Saving…"
                                                    : "Save Show Notes Settings"}
                                            </button>
                                        </form>
                                    )}
                                </div>

                                {adminError && (
                                    <div className="alert alert--error">
                                        {adminError}
                                    </div>
                                )}

                                {loadingUsers && <p>Loading users…</p>}

                                <div className="card" style={{ marginTop: 18 }}>
                                    <h3 className="card__title">
                                        Admin – Overall Change History
                                    </h3>
                                    <p className="card__subtitle">
                                        View episode, file explorer, leads, discovery call, onboarding and auth changes across the portal.
                                    </p>

                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "repeat(auto-fit, minmax(180px, 1fr))",
                                            gap: 10,
                                            marginTop: 10,
                                        }}
                                    >
                                        <label className="form-field">
                                            <span className="form-field__label">Source</span>
                                            <select
                                                name="source"
                                                value={historyFilters.source}
                                                onChange={handleHistoryFilterChange}
                                                className="form-field__select"
                                            >
                                                <option value="">All</option>
                                                <option value="portal">portal</option>
                                                <option value="episode_share">episode_share</option>
                                                <option value="review-link">review-link</option>
                                                <option value="file_explorer">
                                                    file_explorer
                                                </option>
                                                <option value="clients">clients</option>
                                                <option value="podcasts">podcasts</option>
                                                <option value="users">users</option>
                                                <option value="settings">settings</option>
                                                <option value="meeting_requests">
                                                    meeting_requests
                                                </option>
                                                <option value="meeting_requests_public">
                                                    meeting_requests_public
                                                </option>
                                                <option value="leads">leads</option>
                                                <option value="discovery_call">
                                                    discovery_call
                                                </option>
                                                <option value="lead_onboarding">
                                                    lead_onboarding
                                                </option>
                                                <option value="auth">auth</option>
                                            </select>
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">Action</span>
                                            <input
                                                name="action"
                                                value={historyFilters.action}
                                                onChange={handleHistoryFilterChange}
                                                className="form-field__input"
                                                placeholder="e.g. update, upload"
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">User</span>
                                            <select
                                                name="changed_by_user_id"
                                                value={historyFilters.changed_by_user_id}
                                                onChange={handleHistoryFilterChange}
                                                className="form-field__select"
                                            >
                                                <option value="">All users</option>
                                                {sortedUsersForAdminView.map((u) => (
                                                    <option key={u.id} value={String(u.id)}>
                                                        {[u.first_name, u.surname]
                                                            .filter(Boolean)
                                                            .join(" ") || u.email || u.username}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">From</span>
                                            <input
                                                type="date"
                                                name="date_from"
                                                value={historyFilters.date_from}
                                                onChange={handleHistoryFilterChange}
                                                className="form-field__input"
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">To</span>
                                            <input
                                                type="date"
                                                name="date_to"
                                                value={historyFilters.date_to}
                                                onChange={handleHistoryFilterChange}
                                                className="form-field__input"
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">Search</span>
                                            <input
                                                name="search"
                                                value={historyFilters.search}
                                                onChange={handleHistoryFilterChange}
                                                className="form-field__input"
                                                placeholder="Search details"
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">Max rows</span>
                                            <select
                                                name="limit"
                                                value={historyFilters.limit}
                                                onChange={handleHistoryFilterChange}
                                                className="form-field__select"
                                            >
                                                <option value="50">50</option>
                                                <option value="100">100</option>
                                                <option value="250">250</option>
                                                <option value="500">500</option>
                                            </select>
                                        </label>
                                    </div>

                                    <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                                        <button
                                            type="button"
                                            onClick={clearHistoryFilters}
                                            className="btn btn--secondary btn--small"
                                            disabled={historyLoading}
                                        >
                                            Clear
                                        </button>
                                    </div>

                                    {historyError && (
                                        <div className="alert alert--error" style={{ marginTop: 8 }}>
                                            {historyError}
                                        </div>
                                    )}

                                    {!historyLoading && historyLogs.length === 0 && (
                                        <p className="text-description" style={{ marginTop: 10 }}>
                                            No changes found.
                                        </p>
                                    )}

                                    {historyLogs.length > 0 && (
                                        <div style={{ marginTop: 10, overflowX: "auto" }}>
                                            <table className="change-log-table">
                                                <thead>
                                                    <tr>
                                                        <th>When</th>
                                                        <th>User</th>
                                                        <th>Source</th>
                                                        <th>Action</th>
                                                        <th>Episode</th>
                                                        <th>Details</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {historyLogs.map((log) => (
                                                        <React.Fragment key={log.id}>
                                                            <tr
                                                                onClick={() =>
                                                                    setSelectedHistoryLogId(
                                                                        selectedHistoryLogId === log.id
                                                                            ? null
                                                                            : log.id
                                                                    )
                                                                }
                                                                style={{
                                                                    cursor: "pointer",
                                                                    background:
                                                                        selectedHistoryLogId === log.id
                                                                            ? "rgba(59, 130, 246, 0.08)"
                                                                            : undefined,
                                                                }}
                                                            >
                                                                <td>
                                                                    {log.created_at
                                                                        ? new Date(
                                                                            log.created_at
                                                                        ).toLocaleString()
                                                                        : "—"}
                                                                </td>
                                                                <td>{log.changed_by_name || "System"}</td>
                                                                <td>{log.source || "—"}</td>
                                                                <td>{log.action || "—"}</td>
                                                                <td>
                                                                    {log.episode_title
                                                                        ? `${log.episode_title} (#${log.episode_id})`
                                                                        : "—"}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        maxWidth: 460,
                                                                        whiteSpace: "normal",
                                                                        wordBreak: "break-word",
                                                                    }}
                                                                >
                                                                    {summarizeLogDiff(log.diff_json)}
                                                                </td>
                                                            </tr>

                                                            {selectedHistoryLogId === log.id && (
                                                                <tr className="settings-history-detail-row">
                                                                    <td colSpan={6}>
                                                                        <pre className="settings-history-detail-pre">
                                                                            {JSON.stringify(
                                                                                parseHistoryDiff(
                                                                                    log.diff_json
                                                                                ),
                                                                                null,
                                                                                2
                                                                            )}
                                                                        </pre>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

        </PageShell>
    );
}

export default Settings;
